import {useState, useMemo, useCallback} from 'react';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';

export function useVideoActivityReview({
  activityFormData,
  getFormattedDate,
  isEditable,
  editButtonSchema,
  navigate,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  loading
}) {
  const translations = activityFormData?.translations || {};
  const mediaUrls = activityFormData?.mediaUrls || [];
  const countryCode = useAppSelector(selectCountryCode);

  const languages = useMemo(
    () =>
      COUNTRY_LANGUAGES_CODES[countryCode] || [
        {code: 'en', title: 'English', languageId: 1}
      ],
    [countryCode]
  );

  const languageCodes = Object.keys(translations);

  const [imageTabIndex, setImageTabIndex] = useState(0);
  const [videoTabIndex, setVideoTabIndex] = useState(0);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    url: '',
    type: 'image',
    title: ''
  });

  const handlePreview = useCallback((url, type, title) => {
    setPreviewModal({show: true, url, type, title});
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModal({show: false, url: '', type: 'image', title: ''});
  }, []);

  // Image section uses first available language media for thumbnail
  const firstLangCode = languageCodes[0] || 'en';
  const firstMedia = mediaUrls.find((m) => m.language === firstLangCode);

  const getActiveLanguageContent = (tabIndex) => {
    const activeLangCode = languageCodes[tabIndex] || firstLangCode;
    return {
      activeLangCode,
      activeTranslation: translations[activeLangCode] || {}
    };
  };

  // Active language for image text fields (Name, Tag Description)
  const {
    activeLangCode: imageActiveLangCode,
    activeTranslation: imageActiveTranslation
  } = getActiveLanguageContent(imageTabIndex);

  // Active language for video text fields (Description)
  const {
    activeLangCode: videoActiveLangCode,
    activeTranslation: videoActiveTranslation
  } = getActiveLanguageContent(videoTabIndex);

  const displayOrderValue = useMemo(() => {
    const order = activityFormData?.displayOrder;
    if (order === '' || order === null || order === undefined) {
      return '0';
    }
    return String(order);
  }, [activityFormData?.displayOrder]);

  return {
    loading,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    isEditable,
    editButtonSchema,
    navigate,
    activityFormData,
    getFormattedDate,
    languages,
    languageCodes,
    imageTabIndex,
    setImageTabIndex,
    videoTabIndex,
    setVideoTabIndex,
    previewModal,
    handlePreview,
    handleClosePreview,
    firstMedia,
    imageActiveLangCode,
    imageActiveTranslation,
    videoActiveLangCode,
    videoActiveTranslation,
    displayOrderValue
  };
}
